import React, { useState } from 'react'
import { MutationUpdaterFn, Reference } from '@apollo/client'
import { Redirect } from 'react-router-dom'

import {
  DeletePlaceholderTemplateMutation,
  useDeletePlaceholderTemplateMutation
} from '../../../../generated/graphql'
import useModal from '../../../../lib/hooks/useModal'
import { ConfirmDeleteTemplateModal } from '../../templateModals'
import useNotification from '../../../../lib/hooks/useNotification'

type ConfirmDeletePlaceholderModalWrapperProps = {
  /** ID of Placeholder */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
  /** Current placeholder page settings used for redirecting after delete */
  search: string
}

const ConfirmDeletePlaceholderModalWrapper = ({
  id,
  isOpen,
  search
}: ConfirmDeletePlaceholderModalWrapperProps) => {
  const { hideModal } = useModal()
  const [deleteCompleted, setDeleteCompleted] = useState(false)
  const { showErrorNotif } = useNotification()

  const update: MutationUpdaterFn<DeletePlaceholderTemplateMutation> = (cache, { data }) => {
    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        placeholderTemplates(placeholderTemplates: Reference[], { readField }) {
          return placeholderTemplates.filter(
            (placeholderTemplate) =>
              // remove deleted placeholder template
              readField('id', placeholderTemplate) !== data?.deletePlaceholderTemplate.id
          )
        }
      }
    })
  }

  const onCompleted = (data: DeletePlaceholderTemplateMutation): void => {
    if (data && data.deletePlaceholderTemplate) {
      setDeleteCompleted(true)
    }
    hideModal()
  }

  const [deletePlaceholder, mutation] = useDeletePlaceholderTemplateMutation({
    variables: { input: id },
    update,
    onCompleted,
    onError: () => {
      hideModal()
      showErrorNotif('Placeholder could not be deleted.', deletePlaceholder)
    },
    optimisticResponse: {
      __typename: 'Mutation',
      deletePlaceholderTemplate: {
        __typename: 'ResponseStatus',
        id,
        message: '',
        success: true
      }
    }
  })

  const onConfirm = () => {
    deletePlaceholder()
  }

  if (deleteCompleted) {
    return <Redirect to={`/templates/placeholder${search}`} />
  }

  if (!mutation.called) {
    return <ConfirmDeleteTemplateModal isOpen={isOpen} onConfirm={onConfirm} label="Placeholder" />
  }

  return null
}

export default ConfirmDeletePlaceholderModalWrapper
